import { FoldVertical, Play, RotateCcw } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { ACTIVITY_MARK_LABEL } from '@orgus/protocol';
import type { ActivityMark, ActivityMarkKind } from '@orgus/protocol';
import { cn, formatClock, formatDuration, plural } from '@orgus/ui';
import { MARK_GLYPH_PX, activeMs, barPieces, bursts, marksIn, pieceBoxes, usableActivity } from './activity.ts';
import type { BarPiece, RunSummary } from './activity.ts';
import type { TimelineRun } from './model.ts';

/** Each mark kind is its own glyph, so marks never rely on colour (status colours are for status only). */
export const MARK_ICON: Record<ActivityMarkKind, { icon: LucideIcon; className: string }> = {
  resume: { icon: Play, className: 'text-fg-muted' },
  compact: { icon: FoldVertical, className: 'text-fg-muted' },
  reset: { icon: RotateCcw, className: 'text-fg-muted' },
};

export interface PieceTone {
  fill: string;
  edge: string;
  solid: string;
}

/**
 * The inside of a bar with gaps: one block per burst, a dashed line across idle gaps, a
 * dotted one for a running run gone quiet, and a glyph at each resume, compaction or reset.
 * Positions are fractions of the bar's visible extent [from, to]; `px` is its width.
 */
export function BarPieces({ pieces, marks, from, to, px, tone, live, running }: { pieces: BarPiece[]; marks: ActivityMark[]; from: number; to: number; px: number; tone: PieceTone; live: boolean; running: boolean }) {
  const boxes = pieceBoxes(pieces, from, to);
  const lastActive = [...boxes].reverse().find((p) => p.kind === 'active');
  return (
    <span className="absolute inset-0" aria-hidden>
      {boxes.map((p, i) => {
        const { l, r } = p;
        const style = { left: `${l * 100}%`, width: p.kind === 'active' ? `max(${(r - l) * 100}%, 2px)` : `${(r - l) * 100}%` };
        if (p.kind === 'active') {
          const isLast = p === lastActive && running;
          return (
            <span key={i} className={cn('absolute inset-y-0 overflow-hidden rounded-control border', tone.fill, tone.edge)} style={style}>
              {/* The sheen crosses the burst at work now (the stripe, M11), and the grow caret (M2) ends it: static, never blinking. */}
              {isLast && live && <span data-sheen="" className="pointer-events-none absolute inset-y-0 left-0 w-2/5 animate-stripe bg-linear-to-r from-transparent via-fg/15 to-transparent motion-reduce:hidden" />}
              {isLast && live && <span data-caret="" className="absolute inset-y-0 right-0 w-0.5 bg-fg-muted" />}
            </span>
          );
        }
        return (
          <span
            key={i}
            className={cn('absolute top-1/2 -translate-y-1/2 border-t', p.kind === 'idle' ? cn('border-dashed', tone.edge) : 'border-dotted border-border-strong')}
            style={style}
          />
        );
      })}
      {markClusters(marks, from, to, px).map(({ mark: m, count, x }) => {
        const { icon: Icon, className } = MARK_ICON[m.kind];
        return (
          <span
            key={`${m.kind}-${m.at}`}
            data-mark-count={count}
            className={cn('absolute top-1/2 flex h-3.5 min-w-3.5 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1 rounded-pill border border-border-strong bg-surface', count > 1 && 'px-1')}
            style={{ left: `${x * 100}%` }}
          >
            <Icon className={cn('size-2 shrink-0', className)} strokeWidth={3} />
            {count > 1 && <span className="text-caption leading-none text-fg-muted num">{count}</span>}
          </span>
        );
      })}
    </span>
  );
}

/** Marks closer together than this, in px, draw as one glyph with their count. */
const MARK_MERGE_PX = 2 * MARK_GLYPH_PX;
/** A bar narrower than this, in px, draws no marks: the tooltip and the drawer list them. */
const MARK_MIN_BAR_PX = 3 * MARK_GLYPH_PX;

/**
 * Marks as glyphs on a bar `px` wide spanning [from, to], oldest first: marks closer than two
 * glyphs merge into one that carries their count (the newest one's icon), and every glyph sits
 * wholly inside the bar, so none draws past the now line or the panel's edge.
 */
export function markClusters(marks: readonly ActivityMark[], from: number, to: number, px: number): { mark: ActivityMark; count: number; x: number }[] {
  if (px < MARK_MIN_BAR_PX || !marks.length) return [];
  const span = Math.max(1, to - from);
  const at = (t: number) => Math.max(0, Math.min(1, (t - from) / span));
  const out: { mark: ActivityMark; count: number; x: number; first: number }[] = [];
  for (const m of [...marks].sort((a, b) => a.at - b.at)) {
    const x = at(m.at);
    const top = out[out.length - 1];
    if (top && (x - top.first) * px < MARK_MERGE_PX) {
      top.mark = m;
      top.count++;
    } else out.push({ mark: m, count: 1, x, first: x });
  }
  // Half a glyph in from each end, so a glyph never hangs past the bar.
  const half = (MARK_GLYPH_PX / 2) / px;
  return out.map(({ mark, count, x }) => ({ mark, count, x: Math.min(Math.max(x, half), 1 - half) }));
}

/** Half a mark glyph's width plus a little air, in px: a label keeps this far from a mark. */
const MARK_CLEAR_PX = 9;

/**
 * Where a bar with gaps carries its label: the widest stretch of a burst with no mark glyph
 * on it, so a glyph never sits on the text. Fractions of the bar's extent [from, to], which
 * is `px` wide on screen.
 */
export function labelSlot(pieces: readonly BarPiece[], marks: readonly ActivityMark[], from: number, to: number, px: number): { l: number; w: number } | undefined {
  const span = Math.max(1, to - from);
  const pad = px > 0 ? MARK_CLEAR_PX / px : 0;
  const cuts = marks.map((m) => (m.at - from) / span);
  let best: { l: number; w: number } | undefined;
  for (const x of pieces) {
    if (x.kind !== 'active') continue;
    let parts: [number, number][] = [[(Math.max(x.from, from) - from) / span, (Math.min(x.to, to) - from) / span]];
    for (const c of cuts) {
      parts = parts.flatMap(([a, b]): [number, number][] => {
        if (c + pad <= a || c - pad >= b) return [[a, b]];
        const out: [number, number][] = [];
        if (c - pad > a) out.push([a, c - pad]);
        if (b > c + pad) out.push([c + pad, b]);
        return out;
      });
    }
    for (const [a, b] of parts) if (!best || b - a > best.w) best = { l: a, w: b - a };
  }
  return best;
}

/** "Resumed (message from the coordinator)". */
export function markText(m: ActivityMark): string {
  return m.detail ? `${ACTIVITY_MARK_LABEL[m.kind]} (${m.detail})` : ACTIVITY_MARK_LABEL[m.kind];
}

/** "4 bursts, 1h 07m active over 6h 39m", or null for a run with no activity reported. */
export function burstsText(run: Pick<TimelineRun, 'status' | 'startedAt' | 'endedAt' | 'activity'>, now: number): string | null {
  const a = usableActivity(run);
  if (!a) return null;
  const b = bursts(run, now);
  const pieces = barPieces(run, now);
  const first = pieces[0];
  const last = pieces[pieces.length - 1];
  const wall = first && last ? last.to - first.from : 0;
  const active = activeMs(run, now).ms;
  if (b.length <= 1) return `${formatDuration(active)} active`;
  return `${plural(b.length, 'burst')}, ${formatDuration(active)} active over ${formatDuration(wall)}`;
}

/**
 * Marks in a row of the same kind and reason, as one: "Resumed 3 times (message from the
 * coordinator)" at the newest one's time, so a run resumed over and over reads as one line.
 */
export function markRuns(marks: readonly ActivityMark[]): { mark: ActivityMark; count: number }[] {
  const out: { mark: ActivityMark; count: number }[] = [];
  for (const m of [...marks].sort((a, b) => a.at - b.at)) {
    const top = out[out.length - 1];
    if (top && top.mark.kind === m.kind && (top.mark.detail ?? '') === (m.detail ?? '')) {
      top.mark = m;
      top.count++;
    } else out.push({ mark: m, count: 1 });
  }
  return out;
}

/** "Resumed 3 times (message from the coordinator)", or the mark's own text when it is one. */
export function markRunText({ mark: m, count }: { mark: ActivityMark; count: number }): string {
  if (count <= 1) return markText(m);
  return `${ACTIVITY_MARK_LABEL[m.kind]} ${count} times${m.detail ? ` (${m.detail})` : ''}`;
}

/** Tooltip lines about a run's marks, newest last: a fixed time column, then what happened. */
export function ActivityTip({ run }: { run: TimelineRun }) {
  const runs = markRuns(marksIn(run, -Infinity, Infinity));
  if (!runs.length) return null;
  const shown = runs.slice(-3);
  const earlier = runs.slice(0, -3).reduce((n, r) => n + r.count, 0);
  return (
    <span className="flex flex-col gap-1 text-caption text-fg-muted">
      {shown.map((r) => {
        const { icon: Icon, className } = MARK_ICON[r.mark.kind];
        return (
          <span key={`${r.mark.kind}-${r.mark.at}`} className="flex items-start gap-2">
            <span className="flex h-4 shrink-0 items-center">
              <Icon className={cn('size-3', className)} aria-hidden />
            </span>
            <span className="w-16 shrink-0 whitespace-nowrap num">{formatClock(r.mark.at)}</span>
            <span className="min-w-0">{markRunText(r)}</span>
          </span>
        );
      })}
      {earlier > 0 && <span className="text-fg-subtle">{plural(earlier, 'earlier mark')}</span>}
    </span>
  );
}

/** "2 running, 9 done, 1 failed", leaving out zeros; "No agents" when empty. */
export function countsText(s: Pick<RunSummary, 'running' | 'done' | 'failed' | 'stopped' | 'total'>): string {
  const parts = [s.running ? `${s.running} running` : null, s.done ? `${s.done} done` : null, s.failed ? `${s.failed} failed` : null, s.stopped ? `${s.stopped} stopped` : null].filter(Boolean);
  return parts.length ? parts.join(', ') : s.total ? plural(s.total, 'agent') : 'No agents';
}

/** "build:costs, 1h 05m active" or "build:costs, 1h 05m" when activity is not reported. */
export function longestText(s: Pick<RunSummary, 'longest'>): string | null {
  const l = s.longest;
  if (!l || l.ms <= 0) return null;
  return `${l.run.title}, ${formatDuration(l.ms)}${l.measured ? ' active' : ''}`;
}
